import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BAND_COLOR = {
  "Ground Systems": "#A1A1AA",
  "Orbit Prep": "#E0E0E0",
  "Ascent": "#FF9933",
  "Launch-Ready": "#FF9933",
};

export default function Results() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [state, setState] = useState({ loading: true, error: "", report: null, history: [], viewer_owns: false });
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await http.get(`/assessment/results/${id}`);
        setState({ loading: false, error: "", ...r.data });
      } catch (e) {
        setState({ loading: false, error: "Results not found or expired.", report: null, history: [], viewer_owns: false });
      }
    })();
  }, [id]);

  // If a logged-in user is viewing an anonymous report they just completed,
  // attempt to auto-claim it to their account.
  useEffect(() => {
    if (!user) return;
    if (!state.report) return;
    if (state.viewer_owns) return;
    // Only auto-claim if the report has no owner yet
    if (state.report.user_id) return;
    (async () => {
      const attemptId = state.report.attempt_id;
      if (!attemptId) return;
      try {
        await http.post(`/assessment/attempts/${attemptId}/claim`);
        await refresh?.();
        // Reload results to hydrate viewer_owns + history
        const r = await http.get(`/assessment/results/${id}`);
        setState({ loading: false, error: "", ...r.data });
      } catch { /* silent */ }
    })();
  }, [user, state.report, state.viewer_owns, id, refresh]);

  if (state.loading) {
    return (
      <div className="bg-[color:var(--f2-bg)] min-h-screen text-white flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center label-xs">Loading your report…</div>
        <Footer />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="bg-[color:var(--f2-bg)] min-h-screen text-white flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-md text-center hard-border p-10 bg-[color:var(--f2-surface)]">
            <div className="label-xs saffron mb-4">§ Not Found</div>
            <h1 className="font-display uppercase tracking-tighter font-black text-2xl">Report not available.</h1>
            <p className="mt-4 text-[color:var(--f2-muted)]">The link may be invalid. You can take a fresh Forte Score.</p>
            <Link to="/assessment" className="btn-crisp btn-primary mt-6 inline-flex">Take Forte Score →</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { report, history, viewer_owns } = state;
  const scores = report.scores;
  const ai = report.ai_report || {};
  const radarData = Object.entries(scores.dimensions).map(([dim, score]) => ({ dim, score }));

  const claim = async () => {
    setClaiming(true);
    try {
      await http.post(`/assessment/attempts/${report.attempt_id}/claim`);
      const r = await http.get(`/assessment/results/${id}`);
      setState({ loading: false, error: "", ...r.data });
    } catch (e) {
      // ignore
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="bg-[color:var(--f2-bg)] min-h-screen text-white flex flex-col">
      <Nav />
      <div className="flex-1 max-w-[1200px] w-full mx-auto px-6 md:px-10 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="label-xs saffron mb-2">§ Your Forte Score</div>
          <h1 className="font-display uppercase tracking-tighter font-black text-4xl md:text-6xl">
            {(report.profile?.background?.split(" ")?.slice(0,2)?.join(" ") || "You").split(",")[0]},<br/>
            here is your <span className="saffron">readiness map.</span>
          </h1>
        </div>

        {/* Top KPI row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="hard-border p-8 bg-[color:var(--f2-surface)]">
            <div className="label-xs">Overall Forte Score</div>
            <div data-testid="results-overall-score" className="font-display text-7xl font-black tracking-tighter saffron mt-4">
              {scores.overall}
            </div>
            <div className="label-xs mt-3">/ 100</div>
          </div>
          <div className="hard-border p-8 bg-[color:var(--f2-surface)]">
            <div className="label-xs">Band</div>
            <div
              data-testid="results-band"
              className="font-display text-4xl md:text-5xl font-black tracking-tight mt-4 uppercase"
              style={{ color: BAND_COLOR[scores.band] || "#FF9933" }}
            >
              {scores.band}
            </div>
            <div className="label-xs mt-3 text-[color:var(--f2-muted)]">Persona · Individual</div>
          </div>
          <div className="hard-border p-8 bg-[color:var(--f2-surface)]">
            <div className="label-xs">Snapshot</div>
            <div data-testid="results-mini-radar" className="h-40 mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="82%">
                  <PolarGrid stroke="#27272A" />
                  <PolarAngleAxis dataKey="dim" tick={{ fill: "#A1A1AA", fontSize: 9 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} stroke="#27272A" />
                  <Radar dataKey="score" stroke="#FF9933" fill="#FF9933" fillOpacity={0.35} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Full radar + bars */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="hard-border p-8 bg-[color:var(--f2-surface)]">
            <div className="label-xs mb-4">Dimensional Analysis</div>
            <div className="h-[380px]" data-testid="results-radar">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="75%">
                  <PolarGrid stroke="#27272A" />
                  <PolarAngleAxis dataKey="dim" tick={{ fill: "#E0E0E0", fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 100]} stroke="#27272A" tick={{ fill: "#71717A", fontSize: 10 }} />
                  <Radar dataKey="score" stroke="#FF9933" fill="#FF9933" fillOpacity={0.35} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="hard-border p-8 bg-[color:var(--f2-surface)]">
            <div className="label-xs mb-4">Scores by Dimension</div>
            <div className="space-y-4">
              {radarData.map((d) => (
                <div key={d.dim}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[color:var(--f2-muted)]">{d.dim}</span>
                    <span className="saffron font-mono">{d.score}</span>
                  </div>
                  <div className="h-1 bg-[color:var(--f2-border)]">
                    <div className="h-1 bg-[color:var(--f2-saffron)]" style={{ width: `${d.score}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* AI narrative */}
        <div className="hard-border p-8 bg-[color:var(--f2-surface)] mb-4" data-testid="results-ai-narrative">
          <div className="label-xs mb-4">§ AI-generated Report</div>
          {(ai.narrative || "").split(/\n\n+/).map((para, i) => (
            <p key={i} className={`text-base leading-relaxed mb-4 ${i === 0 ? "text-white" : "text-zinc-300"}`}>{para}</p>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {Array.isArray(ai.strongest_dimensions) && ai.strongest_dimensions.length > 0 && (
              <div>
                <div className="label-xs saffron mb-2">Strongest</div>
                <ul className="space-y-1">{ai.strongest_dimensions.map((s, i) => <li key={i} className="text-sm">◆ {s}</li>)}</ul>
              </div>
            )}
            {Array.isArray(ai.weakest_dimensions) && ai.weakest_dimensions.length > 0 && (
              <div>
                <div className="label-xs mb-2">Growth Areas</div>
                <ul className="space-y-1">{ai.weakest_dimensions.map((s, i) => <li key={i} className="text-sm text-[color:var(--f2-muted)]">◆ {s}</li>)}</ul>
              </div>
            )}
          </div>
        </div>

        {/* Recommended roles */}
        {Array.isArray(ai.recommended_roles) && ai.recommended_roles.length > 0 && (
          <div className="hard-border p-8 bg-[color:var(--f2-surface)] mb-4">
            <div className="label-xs mb-4">§ Recommended Roles</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {ai.recommended_roles.map((r, i) => (
                <div key={i} className="hard-border p-4 bg-black/40" data-testid={`results-role-${i}`}>
                  <div className="label-xs saffron mb-1">{r.domain}</div>
                  <div className="font-display font-bold text-lg mb-2">{r.title}</div>
                  <div className="text-xs text-[color:var(--f2-muted)] leading-relaxed">{r.why_fit}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next steps */}
        {Array.isArray(ai.next_steps) && ai.next_steps.length > 0 && (
          <div className="hard-border p-8 bg-[color:var(--f2-surface)] mb-4">
            <div className="label-xs mb-4">§ Suggested Next Steps</div>
            <div className="space-y-3">
              {ai.next_steps.map((s, i) => (
                <div key={i} className="hard-border p-4 bg-black/40 flex items-start justify-between gap-4" data-testid={`results-nextstep-${i}`}>
                  <div>
                    <div className="font-display font-bold text-lg">{s.module}</div>
                    <div className="text-xs text-[color:var(--f2-muted)] mt-1">{s.why}</div>
                  </div>
                  <Link to="/learning" className="btn-crisp shrink-0">Explore →</Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Score over time */}
        {viewer_owns && history?.length > 1 && (
          <div className="hard-border p-8 bg-[color:var(--f2-surface)] mb-4" data-testid="results-history-strip">
            <div className="label-xs mb-4">§ Score Over Time</div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {[...history].reverse().map((r) => (
                <Link
                  to={`/assessment/results/${r.report_id}`}
                  key={r.report_id}
                  className={`shrink-0 hard-border p-4 min-w-[140px] ${r.report_id === report.report_id ? "border-[color:var(--f2-saffron)]" : "hover:border-[color:var(--f2-saffron)]"}`}
                >
                  <div className="label-xs text-[color:var(--f2-muted)]">{new Date(r.created_at).toLocaleDateString()}</div>
                  <div className="font-display font-black text-3xl saffron">{r.scores?.overall}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Save / Login soft-gate */}
        <div className="hard-border p-8 bg-[color:var(--f2-surface)] mb-4">
          {user && viewer_owns ? (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="label-xs saffron">Saved to your account</div>
                <div className="text-sm text-[color:var(--f2-muted)] mt-1">This report is now in your dashboard.</div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Link to="/dashboard" className="btn-crisp">Open dashboard</Link>
                <Link to="/assessment" className="btn-crisp btn-primary">Retake</Link>
              </div>
            </div>
          ) : user && !viewer_owns ? (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="label-xs">Save this report</div>
                <div className="text-sm text-[color:var(--f2-muted)] mt-1">Attach this report to your account.</div>
              </div>
              <button
                data-testid="results-claim-button"
                onClick={claim}
                disabled={claiming}
                className="btn-crisp btn-primary"
              >
                {claiming ? "Saving…" : "Save to my account →"}
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="label-xs">Save & track your progress</div>
                <div className="text-sm text-[color:var(--f2-muted)] mt-1 max-w-lg">
                  Sign in to attach this report to your account, download a PDF, and see your score
                  evolve over time. Your results stay visible at this URL either way.
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Link to="/login" className="btn-crisp btn-primary" data-testid="results-signin-cta">Sign in to save →</Link>
                <Link to="/assessment" className="btn-crisp" data-testid="results-retake-cta">Retake</Link>
              </div>
            </div>
          )}
        </div>

        {/* Share strip */}
        <div className="hard-border p-6 bg-[color:var(--f2-surface)]">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="label-xs text-[color:var(--f2-muted)]">Shareable link · Anyone with this URL can view your report</div>
            <code className="text-xs saffron break-all">{window.location.href}</code>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
